import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo } from "react";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: 'auto',
    width: '100%',
    overflow: 'hidden'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  }
});

export const EconomicsVolumesNpvDataTable = ({ economics }) => {

  // STYLES
  const classes = useStyles();
  const currency = economics?.settings?.currency || 'cad';


  const rows = useMemo(() => {
    try {
      const data = [];
      const fluids = ['oil', 'solutionGas', 'assGas', 'ngl', 'totalBTax', 'incomeTax', 'totalATax'];
      const wellType = economics?.economicVolumesGross?.oil > 0 ? 'oil' : 'gas';


      fluids?.forEach(fluid => {
        data.push({
          id: fluid,
          name: formatCommodityName(fluid), 
          gross: getVolume(fluid, 'gross', wellType), 
          companyGross: getVolume(fluid, 'companyGross', wellType), 
          riGross: 0, 
          companyNet: getVolume(fluid, 'companyNet', wellType), 
          npv0: getNPV(0, fluid, wellType),
          npv5: getNPV(1, fluid, wellType),
          npv10: getNPV(2, fluid, wellType),
          npv15: getNPV(3, fluid, wellType),
          npv20: getNPV(4, fluid, wellType),
          npv25: getNPV(5, fluid, wellType)
        });
      });

      return data;


      function formatCommodityName(fluid) {
        switch(fluid) {
          case 'oil':
            return 'Light and Medium Crude Oil (Mbbl)';
          case 'solutionGas':
            return 'Conventional Nat. Gas (Solution Gas) (MMscf)';
          case 'assGas':
            return 'Conventional Nat. Gas (Non Assoc. & Assoc.) (MMscf)';
          case 'ngl':
            return 'Natural Gas Liquids (Mbbl)';
          case 'condensate':
            return 'Condensate (Mbbl)';
          case 'totalBTax':
            return 'Grand Total b/ Tax (Mboe)';
          case 'totalATax':
            return 'Grand Total a/ Tax (Mboe)';
          case 'incomeTax':
            return 'Income Tax';
          default:
            return '';
        };
      };

      function getVolume(fluid, type, wellType) {
        switch(fluid) {
          case 'oil':
            if (type === 'gross') {
              return Number((economics?.economicVolumesGross?.oil / 1000)?.toFixed(2));
            } else if (type === 'companyGross') {
              return Number((economics?.economicVolumesNet?.oil / 1000)?.toFixed(2));
            } else if (type === 'companyNet') {
              return Number((economics?.economicVolumesCompanyNet?.oil / 1000)?.toFixed(2));
            };
            break;
          case 'solutionGas':
            if (wellType === 'oil') {
              if (type === 'gross') {
                return Number((economics?.economicVolumesGross?.salesGas)?.toFixed(2));
              } else if (type === 'companyGross') {
                return Number((economics?.economicVolumesNet?.salesGas)?.toFixed(2));
              } else if (type === 'companyNet') {
                return Number((economics?.economicVolumesCompanyNet?.salesGas)?.toFixed(2));
              };
            } else {
              return 0;
            };
            break;
          case 'assGas':
            if (wellType === 'gas') {
              if (type === 'gross') {
                return Number((economics?.economicVolumesGross?.salesGas)?.toFixed(2));
              } else if (type === 'companyGross') {
                return Number((economics?.economicVolumesNet?.salesGas)?.toFixed(2));
              } else if (type === 'companyNet') {
                return Number((economics?.economicVolumesCompanyNet?.salesGas)?.toFixed(2));
              };
            } else {
              return 0;
            };
            break;
          case 'ngl':
            if (type === 'gross') {
              return Number(((economics?.economicVolumesGross?.ngl + economics?.economicVolumesGross?.condensate) / 1000)?.toFixed(2));
            } else if (type === 'companyGross') {
              return Number(((economics?.economicVolumesNet?.ngl + economics?.economicVolumesNet?.condensate) / 1000)?.toFixed(2));
            } else if (type === 'companyNet') {
              return Number(((economics?.economicVolumesCompanyNet?.ngl + economics?.economicVolumesCompanyNet?.condensate) / 1000)?.toFixed(2));
            };
            break;
          case 'totalBTax':
          case 'totalATax':
            if (type === 'gross') {
              return Number((economics?.cashflowGross?.reduce((prev, curr) => prev + curr.boe, 0) / 1000)?.toFixed(2));
            } else if (type === 'companyGross') {
              return Number((economics?.cashflowNet?.reduce((prev, curr) => prev + curr.boe, 0) / 1000)?.toFixed(2));
            } else if (type === 'companyNet') {
              let volume = economics?.cashflowNet?.reduce((prev, curr) => prev + curr.boe, 0) / 1000;
              volume -= volume * economics?.settings?.royalty;
              return Number(volume?.toFixed(2));
            };
            break;
          default:
            return 0;
        };
      };

      function getNPV(index, fluid, wellType) {
        switch(fluid) {
          case 'oil':
            let oilNpv = economics?.npvByFluidNet?.byFluid?.oil?.cashflowBTax?.[index] / 1000 || 0;
            if (wellType === 'oil') {
              oilNpv += economics?.npvByFluidNet?.byFluid?.salesGas?.cashflowBTax?.[index] / 1000 || 0;
              oilNpv += economics?.npvByFluidNet?.byFluid?.ngl?.cashflowBTax?.[index] / 1000 || 0;
              oilNpv += economics?.npvByFluidNet?.byFluid?.condensate?.cashflowBTax?.[index] / 1000 || 0;
              oilNpv += economics?.npvByFluidNet?.byFluid?.water?.cashflowBTax?.[index] / 1000 || 0;
            };
            return Number(oilNpv?.toFixed(2)) || 0;
          case 'solutionGas':
            return 0;
          case 'assGas':
            if (wellType === 'gas') {
              let gasNpv = economics?.npvByFluidNet?.byFluid?.salesGas?.cashflowBTax?.[index] / 1000 || 0;
              gasNpv += economics?.npvByFluidNet?.byFluid?.ngl?.cashflowBTax?.[index] / 1000 || 0;
              gasNpv += economics?.npvByFluidNet?.byFluid?.condensate?.cashflowBTax?.[index] / 1000 || 0;
              gasNpv += economics?.npvByFluidNet?.byFluid?.water?.cashflowBTax?.[index] / 1000 || 0;
              return Number(gasNpv?.toFixed(2)) || 0;
            } else {
              return 0;
            };
          case 'ngl':
            return 0;
          case 'totalBTax':
            return Number((economics?.npvNet?.cashflowBTax?.[index] / 1000)?.toFixed(2)) || 0;
          case 'incomeTax':
            return Number((economics?.npvNet?.incomeTax?.[index] / 1000)?.toFixed(2)) || 0;
          case 'totalATax':
            return Number((economics?.npvNet?.cashflowATax?.[index] / 1000)?.toFixed(2)) || 0;
          default:
            return 0;
        };
      };

    } catch (error) {
    };
  }, [economics]);

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'Commodity',
        width: 320,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false,
        type: 'string'
      },
      {
        field: 'gross',
        headerName: 'Gross',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'companyGross',
        headerName: 'Company Gross',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'riGross',
        headerName: 'RI Gross',
        width: 75,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'companyNet',
        headerName: 'Company Net',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv0',
        headerName: `NPV 0% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv5',
        headerName: `NPV 5% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv10',
        headerName: `NPV 10% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv15',
        headerName: `NPV 15% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv20',
        headerName: `NPV 20% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      },
      {
        field: 'npv25',
        headerName: `NPV 25% (M ${currency?.toUpperCase()})`,
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        disableColumnMenu: false
      }
    ];
  }, [currency]);

  try {

    if (rows) {
      return (
        <div className={classes.root}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[10,25,50,100]}
            disableSelectionOnClick
            className={classes.table}
            components={{
              Toolbar: GridToolbar,
            }}
            sx={{fontSize: 12}}
            rowHeight={24}
            autoHeight={true}
          />
        </div>
      );
    } else {
      return <></>;
    };

  } catch (error) {
    return <></>
  };

};
